import { shouldFetch } from "shared/utils";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, reactive, ref } from "vue";
import { fetchBadgeCounts, fetchCurrentUser } from "./auth.api";
import type { CurrentUser } from "./auth.types";

/**
 * Store for managing user data.
 */
export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<CurrentUser | null>(null);

  // Read-only
  const unseenNotificationsCount = computed<number>(
    () => user.value?.unseenNotificationsCount || 0,
  );

  const unseenFeaturesCount = computed<number>(
    () => user.value?.unreadUpdatesCount || 0,
  );

  const openAssignmentsCount = computed<number>(
    () => user.value?.openAssignmentsCount || 0,
  );

  const pendingAssessmentsCount = computed<number>(
    () => user.value?.pendingAssessmentsCount || 0,
  );

  // Open plans the student has not given their own verdict on yet: what is still
  // waiting on them. A plan they assessed themselves drops out, even though the
  // teacher has yet to grade it.
  const openActionplansCount = computed<number>(
    () => user.value?.openActionplansCount || 0,
  );

  // Plans handed in and waiting on this teacher's verdict — what the
  // "Actieplannen beoordelen" pending tab lists.
  const pendingActionplanReviewsCount = computed<number>(
    () => user.value?.pendingActionplanReviewsCount || 0,
  );

  // Gesprekken still waiting on the student: a voorbereiding with a required vraag
  // unanswered, or a gesprek without their own gespreksverslag yet.
  const openConversationsCount = computed<number>(
    () => user.value?.openConversationsCount || 0,
  );

  const refreshBadgeCounts = async () => {
    if (!user.value) {
      return;
    }

    const counts = await fetchBadgeCounts();

    user.value.pendingAssessmentsCount = counts.pending_assessments_count;
    user.value.openAssignmentsCount = counts.open_assignments_count;
    user.value.openActionplansCount = counts.open_actionplans_count;
    user.value.pendingActionplanReviewsCount =
      counts.pending_actionplan_reviews_count;
    user.value.openConversationsCount = counts.open_conversations_count;
  };

  // Fetching state
  const hasFetched: { [key: string]: false | Date } = reactive({
    user: false,
  });

  const isFetching: { [key: string]: false | Promise<unknown> } = reactive({
    user: false,
  });

  const loadCurrentUser = async ({ forceFetch = false } = {}) => {
    if (isFetching["user"]) {
      return isFetching["user"];
    }

    if (!shouldFetch(hasFetched["user"]) && !forceFetch) {
      return user.value || {};
    }

    isFetching["user"] = (async () => {
      try {
        user.value = await fetchCurrentUser();

        hasFetched["user"] = new Date();

        return user.value;
      } catch (error) {
        console.log("🚀 ~ loadCurrentUser ~ error:", error);
      } finally {
        isFetching["user"] = false;
      }
    })();

    return isFetching["user"];
  };

  const resetUnreadFeaturesCount = () => {
    if (!user.value) {
      return;
    }

    user.value.unreadUpdatesCount = 0;
  };

  return {
    user,
    unseenNotificationsCount,
    openAssignmentsCount,
    openActionplansCount,
    openConversationsCount,
    loadCurrentUser,
    unseenFeaturesCount,
    pendingAssessmentsCount,
    pendingActionplanReviewsCount,
    refreshBadgeCounts,
    resetUnreadFeaturesCount,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
